import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Grid, Checkbox, ListItemText, InputLabel, Chip,
  OutlinedInput, TextField, FormControl, Select, Button, MenuItem, Divider
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from '../../Axiosinstance';
import { toast, ToastContainer } from 'react-toastify';

const NewCourse = ({ onFormSubmit }) => {
  const [courseData, setCourseData] = useState({
    name: '',
    fee: '',
    languages: [],
    categoryId: '',
    courseAim: '',
    ps: '',
    entryRequirement: '',
    courseDuration: '',
    courseLocation: '',
    courseAttendanceRecord: '',
    assessmentCriteria: [
      { modeOfTrainingForAssessment: '', mode: '', method: '', duration: '', passingCriteria: '' }
    ],
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const languageOptions = ['English', 'Malay', 'Tamil', 'Hindi', 'Telugu', 'Bengali', 'Mandarin', 'Burmese', 'Thai'];

  // Fetch categories on component mount
  useEffect(() => {
    axios.get('categories/findall')
      .then(response => {
        setCategories(response.data);
      })
      .catch(error => {
        console.error("Error fetching categories:", error);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseData({
      ...courseData,
      [name]: value,
    });
  };

  const handleQuillChange = (field, value) => {
    setCourseData({
      ...courseData,
      [field]: value,
    });
  };

  const handleLanguageChange = (event) => {
    const { value } = event.target;
    setCourseData({
      ...courseData,
      languages: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleDeleteLanguage = (languageToDelete) => {
    setCourseData({
      ...courseData,
      languages: courseData.languages.filter((language) => language !== languageToDelete),
    });
  };

  const handleAssessmentChange = (index, field, value) => {
    const newCriteria = [...courseData.assessmentCriteria];
    newCriteria[index][field] = value;
    setCourseData({
      ...courseData,
      assessmentCriteria: newCriteria,
    });
  };

  const addAssessmentCriteria = () => {
    setCourseData({
      ...courseData,
      assessmentCriteria: [
        ...courseData.assessmentCriteria,
        { modeOfTrainingForAssessment: '', mode: '', method: '', duration: '', passingCriteria: '' },
      ],
    });
  };

  const removeAssessmentCriteria = (index) => {
    const newCriteria = courseData.assessmentCriteria.filter((_, i) => i !== index);
    setCourseData({
      ...courseData,
      assessmentCriteria: newCriteria,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      await axios.post('/wsq/courses', courseData);
      toast.success("Course added successfully!");

      // Reset form
      setCourseData({
        name: '',
        fee: '',
        languages: [],
        categoryId: '',
        courseAim: '',
        ps: '',
        entryRequirement: '',
        courseDuration: '',
        courseLocation: '',
        courseAttendanceRecord: '',
        assessmentCriteria: [
          { modeOfTrainingForAssessment: '', mode: '', method: '', duration: '', passingCriteria: '' }
        ],
      });

      if (onFormSubmit) {
        onFormSubmit();
      }
    } catch (error) {
      toast.error("Error adding course. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onFormSubmit();
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          mt: 5,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: '#f9f9f9',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" gutterBottom>
          Add a New Course
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Course Information</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Course Name"
                name="name"
                value={courseData.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fee"
                name="fee"
                value={courseData.fee}
                onChange={handleChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                name="categoryId"
                value={courseData.categoryId}
                onChange={handleChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Languages"
                name="languages"
                value={courseData.languages}
                onChange={handleLanguageChange}
                required  // Makes the field mandatory
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          onDelete={() => handleDeleteLanguage(value)}
                          deleteIcon={<span>&times;</span>}
                          sx={{ mr: 1 }}
                        />
                      ))}
                    </Box>
                  ),
                }}
                error={courseData.languages.length === 0} // Displays an error if no language is selected
                helperText={courseData.languages.length === 0 ? 'Please select at least one language' : ''}
              >
                {languageOptions.map((language) => (
                  <MenuItem key={language} value={language}>
                    <Checkbox checked={courseData.languages.indexOf(language) > -1} />
                    <ListItemText primary={language} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Course Duration"
                name="courseDuration"
                value={courseData.courseDuration}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Course Location"
                name="courseLocation"
                value={courseData.courseLocation}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Attendance Record"
                name="courseAttendanceRecord"
                value={courseData.courseAttendanceRecord}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Assessment Criteria</Typography>
              {courseData.assessmentCriteria.map((criteria, index) => (
                <Grid container spacing={2} key={index}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mode of Training for Assessment"
                      value={criteria.modeOfTrainingForAssessment}
                      onChange={(e) => handleAssessmentChange(index, 'modeOfTrainingForAssessment', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mode"
                      value={criteria.mode}
                      onChange={(e) => handleAssessmentChange(index, 'mode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Method"
                      value={criteria.method}
                      onChange={(e) => handleAssessmentChange(index, 'method', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Duration"
                      value={criteria.duration}
                      onChange={(e) => handleAssessmentChange(index, 'duration', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Passing Criteria"
                      value={criteria.passingCriteria}
                      onChange={(e) => handleAssessmentChange(index, 'passingCriteria', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>

                    <Button
                      style={{ marginRight: '20px', float: 'right', color: 'white' }}
                      variant="contained"
                      color="error"
                      onClick={() => removeAssessmentCriteria(index)}
                    >
                      Remove Assessment
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button
                style={{ float: 'left', color: 'white' }}
                variant="contained"
                onClick={addAssessmentCriteria}
              >
                Add Assessment
              </Button>
            </Grid>




            <Grid item xs={12}>
              <Typography variant="h6" textAlign='left'>Course Aim</Typography>
              <ReactQuill
                value={courseData.courseAim}
                onChange={(value) => handleQuillChange('courseAim', value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" textAlign='left'>Entry Requirement</Typography>
              <ReactQuill
                value={courseData.entryRequirement}
                onChange={(value) => handleQuillChange('entryRequirement', value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" textAlign='left'>PS</Typography>
              <ReactQuill
                value={courseData.ps}
                onChange={(value) => handleQuillChange('ps', value)}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button variant="contained" type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Course'}
              </Button>
              <Button variant="contained" color="error" onClick={handleCancel} sx={{ ml: 2 }}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
      <ToastContainer />
    </Container>
  );
};

export default NewCourse;
